import { useEffect, useRef, useState, type RefObject } from 'react';
import { AlertTriangle, Check, Eye, EyeOff, Loader2 } from 'lucide-react';
import { nearnessAt, TOUCH_MM, type ClashMarks, type ClashProfile, type Nearness } from './assemblyClash';
import type { AssemblyView } from './assemblyScene';
import type { Assembly } from './assemblyPose';
import { useLanguage } from '../../contexts/LanguageContext';

// WHETHER THE TWO PARTS CAN ACTUALLY BE BROUGHT TOGETHER, said under the movement that
// brings them.
//
// It reads at all times and not on request, which is the whole point of it. A clash is a
// millimetre seen through two parts at one moment out of sixty — nobody catches that by
// watching, and nobody presses a "check" button for a fitting they believe is right. So the
// number is simply there while the clip plays, next to the transport that chooses the
// moment: where the movement is, and how close the surfaces are there.
//
// Reading it costs nothing. The measurement is a profile of the whole step, computed once
// when the fitting or its meshes change (see assemblyClash) and kept (see `measured`), and what
// this does per frame is round t and index an array.

// WHAT HAS BEEN MEASURED ALREADY, kept for as long as the page is open. A profile is an answer
// about one movement of particular models and nothing else, and it was asked again at every
// visit: going back to a sub-step looked at a minute ago was seconds of the same arithmetic for
// the same sixty-one numbers. `key` is everything the answer depends on — the movement and which
// model of each part it was measured on (see fittingModels) — so an edited mesh or a typed seat
// is another key and is measured. The oldest goes when there are too many.
const KEPT = 300;
const measured = new Map<string, ClashProfile>();
const keep = (key: string, profile: ClashProfile) => {
  measured.delete(key);
  measured.set(key, profile);
  if (measured.size > KEPT) measured.delete(measured.keys().next().value!);
};

// The bar's own state, so the phase does not have to hold a profile it never reads.
export function useClash(
  view: RefObject<AssemblyView | null>,
  asm: Assembly,
  step: number,
  ready: boolean,
  // What the measurement depends on, or null for one not worth keeping.
  key: string | null = null,
) {
  const [profile, setProfile] = useState<ClashProfile | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!ready) { setProfile(null); return; }
    const had = key != null ? measured.get(key) : undefined;
    if (had) { setProfile(had); setBusy(false); return; }
    let live = true;
    setBusy(true);
    // A BEAT BEFORE MEASURING. A seat typed digit by digit is four fittings in a second, and
    // three of them are half-typed numbers nobody wants measured.
    const timer = setTimeout(() => {
      void view.current?.clash(asm, step)
        .then((p) => {
          if (key != null) keep(key, p);
          if (live) { setProfile(p); setBusy(false); }
        })
        .catch(() => { if (live) { setProfile(null); setBusy(false); } });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [view, asm, step, ready, key]);

  return { profile, busy };
}

// THE PART OF THE PICTURE THAT IS WRONG, PAINTED RED, at whatever moment is on screen.
//
// ASKED FOR, MOMENT BY MOMENT, and no longer read out of the profile: which faces are inside is
// a walk over every face of both parts, and done for all sixty-one moments it was most of what a
// profile cost, for a patch seen at one of them. So the profile says WHETHER a moment overlaps —
// nothing is asked where it does not — and the faces of one that does are asked once and kept
// for this movement: scrubbing back over it, or playing it a second time, is an array again.
//
// ONE QUESTION AT A TIME, AND THE LATEST. A clip playing passes a moment every frame and an
// answer takes a tenth of a second or two; every moment queued would be painted long after the
// picture had left it. While one is out the paint stays as it was, and the next one asked is
// wherever the picture is by then.
export function useClashPaint(
  view: RefObject<AssemblyView | null>,
  asm: Assembly,
  step: number,
  profile: ClashProfile | null,
  t: number,
  on: boolean,
) {
  // This movement's patches, by sample. A new profile is a new movement, or new meshes.
  const patches = useRef<{ of: ClashProfile | null; at: Map<number, ClashMarks | null> }>({ of: null, at: new Map() });
  const wanted = useRef<number | null>(null);
  const asking = useRef(false);
  const latest = useRef({ asm, step, profile });
  latest.current = { asm, step, profile };

  useEffect(() => {
    if (patches.current.of !== profile) patches.current = { of: profile, at: new Map() };
    const n = profile?.samples.length ?? 0;
    const i = Math.round(Math.min(1, Math.max(0, t)) * (n - 1));
    if (!on || !profile || !n || profile.samples[i].kind !== 'overlap') {
      wanted.current = null;
      view.current?.clashMarks(null);
      return;
    }
    const kept = patches.current.at;
    if (kept.has(i)) { wanted.current = null; view.current?.clashMarks(kept.get(i) ?? null); return; }
    wanted.current = i;
    const next = () => {
      const k = wanted.current;
      const now = latest.current;
      if (asking.current || k == null || !view.current || !now.profile) return;
      asking.current = true;
      const settle = (marks: ClashMarks | null | undefined) => {
        asking.current = false;
        // An answer about a movement the picture has left says nothing about this one.
        if (latest.current.profile !== now.profile) { next(); return; }
        if (marks !== undefined) patches.current.at.set(k, marks);
        if (wanted.current === k) {
          wanted.current = null;
          view.current?.clashMarks(marks ?? null);
        } else next();
      };
      void view.current.clashFaces(now.asm, now.step, k / (now.profile.samples.length - 1))
        .then(settle, () => settle(undefined));
    };
    next();
    // `asm` and `step` are read through `latest`: the profile is what says they changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, profile, t, on]);
}

const mm = (v: number) => (Math.abs(v) < 10 ? Math.abs(v).toFixed(2) : Math.abs(v).toFixed(1));

// The four readings, each in the colour it means. Overlap is the only one that is a problem,
// so it is the only one that is red — a phase where three quarters of the readings are
// coloured like a warning is a phase nobody reads.
const TONE: Record<Nearness['kind'], string> = {
  clear: 'text-blue-600',
  touching: 'text-sky-600',
  overlap: 'text-red-600',
  engaged: 'text-slate-500',
};
const DOT: Record<Nearness['kind'], string> = {
  clear: 'bg-blue-500',
  touching: 'bg-sky-500',
  overlap: 'bg-red-500',
  engaged: 'bg-slate-400',
};
const FILL: Record<Nearness['kind'], string> = {
  clear: 'fill-blue-500',
  touching: 'fill-sky-500',
  overlap: 'fill-red-500',
  engaged: 'fill-slate-400',
};

// THE WHOLE MOVEMENT AS A SHAPE — every measured moment left to right, how close the parts
// are up and down, and the line where they meet across the middle.
//
// The readout above says where the clip is standing NOW, which is one number out of
// sixty-one and cannot say whether that number is the good part of a bad movement. The shape
// can: a fitting that dips under the line for a moment and comes back is a part passing
// through another and returning, and it looks like exactly that here.
//
// AND IT IS THE TRANSPORT. Pressing a moment on the graph stops the clip on it — which is
// the gesture the whole phase is built around, arrived at from the measurement rather than
// from the scrubber: you see where it goes wrong, and you press there to look at it.

const W = 600;
const H = 172;
// The plot's own rectangle inside that. The left margin is the width of the millimetre
// labels and the bottom one the height of the moment labels: an axis with no numbers on it
// is a shape, and what this has to be is a measurement.
const L = 46;
const R = 10;
const TOP = 16;
const BOT = 26;

// MILLIMETRES, SPACED SO THE MILLIMETRE THAT MATTERS CAN BE SEEN.
//
// Linear would be honest and useless: a part starts 37 mm out and goes wrong by three
// quarters of one, so the whole of what this graph exists to show would be 2% of its height
// and under the axis line. Logarithmic from the tolerance outwards — a decade per unit,
// starting at the 0.25 mm that separates touching from overlapping — puts that same three
// quarters of a millimetre a third of the way down. Symmetric about zero, so a gap and an
// overlap of the same size are the same distance from the line.
//
// Which is exactly why the axis is LABELLED rather than left to be inferred: a reader who
// assumes the spacing is linear reads every distance on it wrong.
const scale = (v: number) => Math.sign(v) * Math.log10(1 + Math.abs(v) / TOUCH_MM);

// THE MILLIMETRE VALUES WORTH WRITING ON THE AXIS, biggest first — a rung every half decade,
// which on a log scale is an even spacing, and the tolerance itself at the bottom because it
// is the line the whole graph is about.
//
// GENERATED RATHER THAN LISTED. It was [10, 3, 1, TOUCH_MM], which was every rung there could
// be while the axis stopped at 30. It goes as high as the movement does now — a part brought
// in from 27 cm away ends the axis at 300 — and a fixed list would have left the whole top of
// that graph unlabelled.
const rungsBelow = (top: number): number[] => {
  const out: number[] = [];
  // Down to a millimetre and no further: under that the only number worth a line is the
  // tolerance itself, which is added below and is what the band either side of zero is drawn
  // from. Rungs at 0.3 and 0.1 would be three labels in the space of one, all of them inside
  // the region the graph calls touching.
  for (let decade = Math.floor(Math.log10(top)); decade >= 0; decade -= 1) {
    for (const step of [3, 1]) {
      const v = step * 10 ** decade;
      // NOT TOO NEAR THE TOP, which is drawn and labelled whatever it is: 35 with a rung at 30
      // prints two numbers a hair apart and a reader cannot tell which line is which. A rung
      // is worth a line when it stands clear of the end of the axis, measured where it is
      // drawn — up the log scale — rather than in millimetres.
      if (v < top && (scale(top) - scale(v)) / scale(top) > 0.06) out.push(v);
    }
  }
  out.push(TOUCH_MM);
  return out;
};

// WHERE THE AXIS ENDS, and it is a round number rather than the reading's own maximum.
//
// The end of an axis is a claim about how far the scale goes, so it has to be a number that
// can be written down. Ending at the data instead put the furthest dot hard against the top
// of the plot with nothing labelling the height it had reached: a 12.2 mm gap sat above a
// line marked 10, and the only thing on screen saying how far above was the dot itself.
//
// IT USED TO STOP AT 30 — REACH_MM, the distance past which the exact gap is not worth
// measuring — and every sample beyond it was drawn AT 30. A rail brought in from 27 cm away
// therefore drew a flat line along the ceiling for the whole of its travel: not a movement
// with nothing to report, a movement none of which fitted on the graph.
//
// So the top follows the reading, rounded up to something a person would have chosen: two
// significant figures on a grid of five — 8 ends at 10, 13 at 15, 32 at 35, 270 at 300. Below
// 5 mm the tolerance ladder takes over, because a fifth of a millimetre either way is the
// scale this graph is actually about and rounding those up to 5 would bury them.
const CLOSE = [TOUCH_MM, 0.5, 1, 2, 3, 5];
const ceilRung = (v: number) => {
  if (v <= 5) return CLOSE.find((r) => r >= v - 1e-9) ?? 5;
  const step = 5 * 10 ** Math.max(0, Math.floor(Math.log10(v)) - 1);
  return Math.ceil(v / step - 1e-9) * step;
};
// And the moments worth writing under it — the transport's own quarters, in the two decimals
// the transport prints.
const MOMENTS = [0, 0.25, 0.5, 0.75, 1];

const label = (v: number) => (Math.abs(v) < 1 ? v.toFixed(2) : String(v));

function ClashGraph({ profile, t, onSeek }: {
  profile: ClashProfile;
  t: number;
  onSeek: (t: number) => void;
}) {
  const { t: tr } = useLanguage();
  const n = profile.samples.length;
  const at = (i: number) => L + (i / (n - 1)) * (W - L - R);
  const atT = (v: number) => L + v * (W - L - R);

  // AS MEASURED, WHATEVER IT COMES TO. Every sample used to be clamped to REACH_MM before it
  // was drawn, which was right while the axis stopped there and is a lie now: the reading
  // itself goes past it (beyond REACH_MM the gap is the parts' bounding boxes rather than
  // their surfaces — coarser, and the only thing this graph does with it is place a dot high
  // up), and the readout above has always printed it.
  const said = (s: Nearness) => (Number.isFinite(s.mm) ? s.mm : null);

  // HOW MUCH OF THE HEIGHT EACH SIDE OF CONTACT GETS. Whatever the reading needs, but never
  // less than a millimetre either way: the limit line lives at a quarter of one, and a
  // movement that never goes near it would otherwise squeeze the whole of the region it must
  // not enter into the axis line itself.
  const measured = profile.samples.map(said).filter((v): v is number => v != null);
  const topUp = ceilRung(Math.max(...measured, 1));
  const topDown = ceilRung(Math.max(...measured.map((v) => -v), 1));
  // A moment nothing could be measured at is drawn at the top of the axis, which is what "no
  // two surfaces anywhere near each other" looks like on a graph of how near they are.
  const val = (s: Nearness) => said(s) ?? topUp;
  const up = scale(topUp);
  const down = scale(topDown);
  const plot = H - TOP - BOT;
  const zeroY = TOP + plot * (up / (up + down));
  const y = (v: number) => {
    const f = scale(v);
    return f >= 0 ? zeroY - (f / up) * (zeroY - TOP) : zeroY + (-f / down) * (H - BOT - zeroY);
  };

  // The end of each half, then the sparse rungs below it — so the topmost number on the axis
  // is always the height the axis actually goes to.
  const rungs = [
    topUp,
    ...rungsBelow(topUp),
    0,
    ...[topDown, ...rungsBelow(topDown)].map((v) => -v),
  ];

  // The line breaks over a thread that has bitten: there is no measurement there, and joining
  // the two sides would draw one across a stretch nobody measured.
  const runs: string[] = [];
  let run: string[] = [];
  for (const [i, s] of profile.samples.entries()) {
    if (s.kind === 'engaged') { if (run.length) { runs.push(run.join(' ')); run = []; } continue; }
    run.push(`${at(i).toFixed(1)},${y(val(s)).toFixed(1)}`);
  }
  if (run.length) runs.push(run.join(' '));

  const seek = (e: React.MouseEvent<SVGSVGElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - box.left) / box.width) * W;
    const i = Math.round(((x - L) / (W - L - R)) * (n - 1));
    onSeek(Math.min(1, Math.max(0, i / (n - 1))));
  };

  const now = Math.round(t * (n - 1));

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="mt-2 h-auto w-full cursor-pointer"
      onClick={seek}
      role="img"
      aria-label={tr('notice.clash.graph')}
    >
      {/* BELOW THE LINE IS THROUGH EACH OTHER — tinted rather than labelled, so the graph
          says which way is wrong without a word on it. */}
      <rect x={L} y={zeroY} width={W - L - R} height={H - BOT - zeroY} className="fill-red-500/5" />
      {/* AND THE BAND EITHER SIDE OF IT IS TOUCHING, which is what parts in an assembly are
          supposed to do. */}
      <rect x={L} y={y(TOUCH_MM)} width={W - L - R} height={y(-TOUCH_MM) - y(TOUCH_MM)} className="fill-sky-500/10" />

      {/* THE MILLIMETRES, WRITTEN. A rung, its number, and a hairline across the plot to read
          a dot against. */}
      {rungs.map((v) => (
        <g key={v}>
          <line
            x1={L}
            x2={W - R}
            y1={y(v)}
            y2={y(v)}
            className={v === 0 ? 'stroke-slate-400' : 'stroke-slate-200'}
            strokeWidth={1}
          />
          <text
            x={L - 6}
            y={y(v) + 3.5}
            textAnchor="end"
            fontSize={10}
            className="fill-slate-700 tabular-nums"
          >
            {label(v)}
          </text>
        </g>
      ))}
      {/* What those numbers are, said once, at the top of the axis they run down. */}
      <text x={L - 6} y={TOP - 5} textAnchor="end" fontSize={9} className="fill-slate-700">
        {tr('notice.clash.axisY')}
      </text>

      {/* THE LIMIT. Not the contact line — parts are meant to touch — but the quarter of a
          millimetre past it, where touching stops being an explanation and the two are
          through each other. A dot under this line is the fitting being wrong. */}
      <line x1={L} x2={W - R} y1={y(-TOUCH_MM)} y2={y(-TOUCH_MM)} className="stroke-red-500" strokeWidth={1.5} />
      <text x={W - R} y={y(-TOUCH_MM) - 4} textAnchor="end" fontSize={9} className="fill-red-500">
        {tr('notice.clash.limit')}
      </text>

      {/* THE MOMENTS, WRITTEN — the same 0 to 1 the scrubber under the picture is marked in,
          so a number read here can be typed there. */}
      {MOMENTS.map((v) => (
        <g key={v}>
          <line x1={atT(v)} x2={atT(v)} y1={TOP} y2={H - BOT} className="stroke-slate-200" strokeWidth={1} />
          <text
            x={atT(v)}
            y={H - BOT + 13}
            textAnchor="middle"
            fontSize={10}
            className="fill-slate-700 tabular-nums"
          >
            {v.toFixed(2)}
          </text>
        </g>
      ))}
      <text x={W - R} y={H - 2} textAnchor="end" fontSize={9} className="fill-slate-700">
        {tr('notice.clash.axisX')}
      </text>
      <line x1={L} x2={W - R} y1={H - BOT} y2={H - BOT} className="stroke-slate-400" strokeWidth={1} />
      <line x1={L} x2={L} y1={TOP} y2={H - BOT} className="stroke-slate-400" strokeWidth={1} />

      {runs.map((points) => (
        <polyline
          key={points.slice(0, 24)}
          points={points}
          fill="none"
          className="stroke-slate-400"
          strokeWidth={1.5}
        />
      ))}

      {/* WHERE THE CLIP IS STANDING, so the graph and the scrubber under the picture are
          visibly the same movement. */}
      <line
        x1={at(now)}
        x2={at(now)}
        y1={TOP}
        y2={H - BOT}
        className="stroke-slate-400"
        strokeWidth={1}
        strokeDasharray="3 3"
      />

      {profile.samples.map((s, i) => (
        <circle
          key={i}
          cx={at(i)}
          cy={s.kind === 'engaged' ? zeroY : y(val(s))}
          r={i === now ? 4.5 : 2.5}
          className={s.kind === 'engaged' ? 'fill-none stroke-slate-400' : FILL[s.kind]}
          strokeWidth={s.kind === 'engaged' ? 1.2 : 0}
        />
      ))}
    </svg>
  );
}

export function ClashBar({
  profile,
  busy,
  t,
  onSeek,
  marked,
  onMarked,
}: {
  profile: ClashProfile | null;
  busy: boolean;
  // WHETHER WHAT IS THROUGH SOMETHING IS PAINTED RED ON THE PARTS, and the switch for it: the red is
  // how an overlap is found, and it is also a button drawn red all over by a tenth of a millimetre
  // of a fit that is meant to be tight.
  marked: boolean;
  onMarked: (on: boolean) => void;
  // Where the transport is, so the left half is about the frame on screen.
  t: number;
  // And how to put it somewhere else, for the button that goes to the worst moment.
  onSeek: (t: number) => void;
}) {
  const { t: tr } = useLanguage();
  const here = profile ? nearnessAt(profile, t) : null;
  const nothing = !!profile && !profile.samples.length;
  const full = !!profile && !busy && !nothing;

  // A PLACING HAS NOTHING TO MEASURE AND SAYS SO, in a box the size of the sentence. It is
  // the one state that never becomes a graph — there is no second part coming — so nothing
  // is being kept still for, and a panel stretched to the page around six words is a large
  // empty rectangle announcing that nothing has happened.
  if (nothing) {
    return (
      <div className="mx-auto w-auto rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
        {tr('notice.clash.nothing')}
      </div>
    );
  }

  // MEASURING LOOKS LIKE THE MEASUREMENT, to the pixel: the same width, the same three bands,
  // and the graph's own height held open by an empty box of the same proportions.
  //
  // Not tidiness — the panel sits under the transport, and a panel that grows by a hundred
  // pixels when the answer lands pushes the page under the hand that is scrubbing. The
  // measurement re-runs on every edit to the fitting, so that jump would happen on every
  // number typed into the panel above.
  return (
    <div className="relative mx-auto flex w-full max-w-3xl flex-col rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
      <button
        type="button"
        onClick={() => onMarked(!marked)}
        aria-pressed={marked}
        title={tr(marked ? 'notice.clash.hideMarks' : 'notice.clash.showMarks')}
        aria-label={tr(marked ? 'notice.clash.hideMarks' : 'notice.clash.showMarks')}
        className={`absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[11px] font-medium transition-colors ${
          marked ? 'border-red-200 bg-white text-red-600 hover:border-red-400' : 'border-slate-300 bg-white text-slate-500 hover:border-blue-500 hover:text-blue-600'
        }`}
      >
        {marked ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        {tr('notice.clash.marks')}
      </button>
      {/* WHERE THE MOVEMENT IS NOW, over the graph — the moment the dashed line is standing
          on, in words, above the picture of every other moment. A fixed height, so the line
          that is empty while measuring is exactly as tall as the line that replaces it. */}
      <div className="flex h-6 w-full flex-wrap items-center justify-center gap-x-4">
        {full && here ? (
          <span className={`flex items-center gap-2 text-sm font-semibold ${TONE[here.kind]}`}>
            <span className={`h-2 w-2 shrink-0 rounded-full ${DOT[here.kind]}`} />
            {tr(`notice.clash.${here.kind}`)}
            {/* The millimetres, where there are any to give: a thread that has bitten is
                measured against a bore its mesh does not have, and a part still 60 mm out is
                far rather than 61.3 mm far. */}
            {here.kind !== 'engaged' && Number.isFinite(here.mm) && (
              <span className="font-mono text-xs font-normal tabular-nums text-slate-500">
                {here.mm < 0
                  ? tr('notice.clash.deep').replace('{mm}', mm(here.mm))
                  : tr('notice.clash.apart').replace('{mm}', mm(here.mm))}
              </span>
            )}
          </span>
        ) : null}
      </div>

      {/* AND THE WHOLE OF IT, PRESSABLE — or, until it is measured, the room it will take.
          The empty box carries the graph's own proportions rather than a height in pixels:
          the picture is an SVG scaled to the panel's width, so only a ratio holds the same
          space at every width the page can be. */}
      {full && profile.samples.length > 1 ? (
        <ClashGraph profile={profile} t={t} onSeek={onSeek} />
      ) : (
        <div className="mt-2 flex aspect-[600/172] w-full items-center justify-center">
          <span className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            {tr('notice.clash.measuring')}
          </span>
        </div>
      )}

      {/* AND WHAT THE WHOLE OF IT COMES TO, under the graph it is the verdict on — which is
          the reading that decides whether the fitting is signable, and cannot be got by
          watching one frame. Its own fixed height, for the same reason as the line above. */}
      <div className="flex h-6 w-full items-center justify-center">
        {full && (
          profile.worst ? (
            <span className="flex items-center gap-2 text-sm font-semibold text-red-600">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {tr('notice.clash.worst')
                .replace('{mm}', mm(profile.worst.mm))
                .replace('{t}', profile.worst.t.toFixed(2))}
              <button
                type="button"
                onClick={() => onSeek(profile.worst!.t)}
                className="rounded-md border border-red-400 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-600 transition-colors hover:bg-red-50"
              >
                {tr('notice.clash.goto')}
              </button>
            </span>
          ) : (
            <span className="flex items-center gap-2 text-sm font-semibold text-blue-600">
              <Check className="h-4 w-4 shrink-0" />
              {tr('notice.clash.none')}
            </span>
          )
        )}
      </div>
    </div>
  );
}
